<div class="flex flex-col h-full overflow-hidden">
    <!-- Timeline Controls Bar -->
    <div class="bg-white border border-gray-200 rounded-xl shadow-sm mb-4 overflow-hidden flex-shrink-0">
        <div class="flex items-center justify-between px-5 py-3">
            <!-- Left: Section Title with Icon -->
            <div class="flex items-center gap-3">
              <div class="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
                <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
              </div>
                <div>
                    <h2 class="text-base font-bold text-gray-900">Task Timeline</h2>
                    <p class="text-xs text-gray-500">{{ formattedDate }}</p>
                </div>
            </div>

            <!-- Right: Controls -->
            <div class="flex items-center gap-3">
                <!-- Calendar Toggle (only when connected) -->
                @if (googleCalendar.connected()) {
                <button (click)="showCalendarEvents = !showCalendarEvents"
                        [class]="showCalendarEvents
                          ? 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 transition-all cursor-pointer'
                          : 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-white text-gray-500 border border-gray-200 hover:bg-gray-50 transition-all cursor-pointer'"
                        title="Toggle Google Calendar events">
                    <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19 3h-1V1h-2v2H8V1H6v2H5C3.9 3 3 3.9 3 5v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V9h14v10zM5 7V5h14v2H5zm2 4h10v2H7zm0 4h7v2H7z"/>
                    </svg>
                    <span>Calendar</span>
                    <span [class]="showCalendarEvents ? 'w-1.5 h-1.5 rounded-full bg-blue-500' : 'w-1.5 h-1.5 rounded-full bg-gray-300'"></span>
                </button>
                }

                <!-- Divider -->
                <div class="w-px h-6 bg-gray-200"></div>

                <!-- Date Navigation -->
                <div class="flex items-center gap-1 bg-gray-50 rounded-lg p-1">
                    <button (click)="navigateDay(-1)"
                            class="p-1.5 rounded-md hover:bg-white hover:shadow-sm transition-all text-gray-500 hover:text-gray-700 cursor-pointer"
                            title="Previous day">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
                        </svg>
                    </button>
                    <kendo-datepicker
                        preventScrollPropagation
                        [value]="selectedDateValue"
                        (valueChange)="onDatePickerChange($event)"
                        [format]="'dd/MM/yyyy'"
                        [navigation]="true"
                        [weekNumber]="true"
                        [popupSettings]="{ popupClass: 'kendo-datepicker-large' }"
                        placeholder="Select date"
                        class="w-[140px]">
                    </kendo-datepicker>
                    <button (click)="navigateDay(1)"
                            class="p-1.5 rounded-md hover:bg-white hover:shadow-sm transition-all text-gray-500 hover:text-gray-700 cursor-pointer"
                            title="Next day">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                        </svg>
                    </button>
                    <button (click)="goToToday()"
                            class="px-2 py-1.5 rounded-md text-xs font-medium text-blue-600 hover:bg-blue-50 transition-all cursor-pointer"
                            title="Go to today">
                        Today
                    </button>
                </div>

                <!-- Divider -->
                <div class="w-px h-6 bg-gray-200"></div>

                <!-- Zoom/Interval Selector -->
                <div class="flex items-center gap-2">
                    <span class="text-xs text-gray-500 font-medium">Interval:</span>
                    <div class="relative">
                        <select [(ngModel)]="selectedZoom" (change)="onConfigChange()"
                                class="appearance-none pl-3 pr-8 py-1.5 rounded-lg text-sm font-medium text-gray-600
                                       bg-white border border-gray-200 cursor-pointer
                                       hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400
                                       transition-all">
                            @for(res of zoomOptions; track res) {
                                <option [ngValue]="res">{{ res }} min</option>
                            }
                        </select>
                        <svg class="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                        </svg>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Two-Column Timeline Content -->
    <div class="flex flex-1 overflow-hidden gap-3 relative min-h-0">
        <!-- Loading Overlay -->
        @if(isLoadingTasks || isLoadingEntries) {
            <div class="absolute inset-0 bg-white/60 backdrop-blur-sm z-50 flex items-center justify-center rounded-xl">
                <div class="flex flex-col items-center gap-3">
                    <div class="relative">
                        <!-- Spinner -->
                        <div class="w-12 h-12 border-4 border-gray-200 border-t-blue-500 rounded-full animate-spin"></div>
                    </div>
                    <div class="text-sm font-medium text-gray-600">
                        @if(isInitialLoad) {
                            Loading timeline...
                        } @else {
                            Refreshing data...
                        }
                    </div>
                    <div class="text-xs text-gray-400">
                        @if(isLoadingTasks && isLoadingEntries) {
                            Loading tasks and entries
                        } @else if(isLoadingTasks) {
                            Loading tasks
                        } @else {
                            Loading entries
                        }
                    </div>
                </div>
            </div>
        }

        <!-- Activity Column (Left) -->
        <div [style.width.px]="leftWidth" class="h-full">
            <app-activity-log
                [tasks]="timelineTasks"
                [slots]="timeSlots"
                [zoom]="selectedZoom"
                [rowHeight]="rowHeight"
                [currentLineTop]="currentLineTop"
                [hoveredSlotIndex]="hoveredSlotIndex"
                [calendarEvents]="showCalendarEvents ? calendarEvents() : []"
                [calendarLoading]="showCalendarEvents && calendarLoading()"
                [calendarColors]="calendarColorMap()"
                (hoverSlot)="onSlotHover($event)">
            </app-activity-log>
        </div>

        <!-- Resize Handle -->
        <div class="w-1.5 cursor-col-resize rounded-full bg-gray-200 hover:bg-blue-400
                    transition-colors duration-200 flex-shrink-0 my-2"
             (mousedown)="startResize($event)">
        </div>

        <!-- Time Entries Column (Right) -->
        <div [style.width.px]="rightWidth" class="h-full flex-1">
            <app-time-grid
                [zoom]="selectedZoom"
                [timeEntries]="timeEntries"
                [slots]="timeSlots"
                [rowHeight]="rowHeight"
                [currentLineTop]="currentLineTop"
                [hoveredSlotIndex]="hoveredSlotIndex"
                (selectionMade)="handleSelection($event)"
                (hoverSlot)="onSlotHover($event)">
            </app-time-grid>
        </div>
    </div>
</div>


